import {
  APP_INITIALIZER,
  ErrorHandler,
  Provider,
  enableProdMode,
  importProvidersFrom,
  isDevMode,
} from '@angular/core';
import * as Sentry from '@sentry/angular';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, provideRouter, withInMemoryScrolling } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';

import {
  ANGULARTICS2_TOKEN,
  AngularRouterTracking,
  Angulartics2,
  Angulartics2Settings,
  RouterlessTracking,
} from 'angulartics2';

import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { providePwaAppInstaller } from './app/toExtract/pwa/installer';
import { providePwaAppVersionWatcher } from './app/toExtract/pwa/version-watcher';
import { providePwaViewportFix } from './app/toExtract/pwa/viewport-fix';
import { environment } from './environments/environment';

const sentryProviders: Provider[] = [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: true,
    }),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  {
    provide: APP_INITIALIZER,
    useFactory: () => noopFn,
    deps: [Sentry.TraceService],
    multi: true,
  },
];

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noopFn = () => {};

function shouldEnableServiceWorker() {
  return (
    (!isDevMode() && environment.stage !== 'dev') ||
    environment.stage === 'devMobile'
  );
}

if (environment.stage === 'prod') {
  enableProdMode();
  initErrorMonitoring();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(
      APP_ROUTES,
      withInMemoryScrolling({ anchorScrolling: 'enabled' })
      // withDebugTracing()
    ),
    importProvidersFrom(
      BrowserModule,
      MatButtonModule,
      MatIconModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: shouldEnableServiceWorker(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      }),
      MatSnackBarModule
    ),
    ...sentryProviders,
    providePwaAppInstaller(),
    providePwaAppVersionWatcher(),
    providePwaViewportFix(),
    provideAnimations(),
    provideAngulartics({
      developerMode: environment.stage !== 'prod',
    }),
  ],
}).catch((err) => console.error(err));

function initErrorMonitoring() {
  Sentry.init({
    dsn: 'https://bad5f3249f7d4f4a990b63dc00f60f05@o935149.ingest.sentry.io/5884831',
    integrations: [Sentry.browserTracingIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
  });
}

function provideAngulartics(
  settings: Partial<Angulartics2Settings>
): Provider[] {
  return [
    { provide: ANGULARTICS2_TOKEN, useValue: { settings } },
    { provide: RouterlessTracking, useClass: AngularRouterTracking },
    Angulartics2,
  ];
}
